import { resolveSemanticColor } from '@/theme/semanticTokens';
import { r, s } from '@/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';

type BuildGlobalSearchPopoverPaperSxArgs = {
  width: number | string;
};

type BuildGlobalSearchStatusRowSxArgs = {
  height: number;
};

type BuildGlobalSearchSearchFieldSxArgs = {
  inputAdornmentIconSize: number;
  isCompact: boolean;
  toolbarSearchBorderColor: string;
  toolbarSearchFontSize: number;
  toolbarSearchHeight: number;
  toolbarSearchRadius: string;
  toolbarSearchWidthPx: string;
};

type BuildGlobalSearchDesktopShellSxArgs = {
  desktopSearchExpanded: boolean;
  toolbarSearchHeight: number;
  toolbarSearchWidth: number;
};

type BuildGlobalSearchDesktopFieldFrameSxArgs = {
  toolbarSearchWidth: number;
};

type BuildGlobalSearchDesktopIconButtonSxArgs = {
  toolbarSearchButtonIconSize: number;
  toolbarSearchHeight: number;
};

type BuildGlobalSearchResultIconSxArgs = {
  resultIconContainerSize: number;
};

type BuildGlobalSearchResultGroupSxArgs = {
  isFirstGroup: boolean;
};

type BuildGlobalSearchResultItemSxArgs = {
  selected: boolean;
};

type BuildGlobalSearchResultIconFrameSxArgs = {
  resultIconContainerSize: number;
  selected: boolean;
};

export const globalSearchPopoverRootSx: SxProps<Theme> = {
  pointerEvents: 'none',
};

export const globalSearchResultsListSx: SxProps<Theme> = {
  p: s('none'),
};

export const globalSearchCompactFieldFrameSx: SxProps<Theme> = {
  mb: s('sm'),
};

export const globalSearchResultTextFrameSx: SxProps<Theme> = {
  minWidth: 0,
  flex: 1,
};

export const globalSearchResultTitleSx: SxProps<Theme> = {
  minWidth: 0,
};

export const globalSearchResultMetadataSx: SxProps<Theme> = {
  mt: s('xxs'),
};

export const globalSearchInheritedTextSx: SxProps<Theme> = {
  color: 'inherit',
};

export const globalSearchResultIconSx: SxProps<Theme> = {
  fontSize: 17,
};

export const globalSearchLoadingTextSx: SxProps<Theme> = (theme) => ({
  fontSize: theme.typography.subtitle1.fontSize,
});

export const globalSearchErrorTextSx: SxProps<Theme> = {
  color: 'error.main',
};

export const globalSearchHighlightSx: SxProps<Theme> = (theme) => ({
  color: theme.app.colors.primary,
});

export const buildGlobalSearchPopoverPaperSx = ({
  width,
}: BuildGlobalSearchPopoverPaperSxArgs): SxProps<Theme> => ({
  mt: s('none'),
  width,
  maxWidth: `calc(100vw - ${s('2xl')})`,
  maxHeight: 'min(70vh, 520px)',
  overflowY: 'auto',
  borderRadius: `${r('card')} !important`,
  pointerEvents: 'auto',
});

export const buildGlobalSearchResultsSummarySx =
  ({ height }: BuildGlobalSearchStatusRowSxArgs): SxProps<Theme> =>
  (theme) => ({
    height,
    display: 'flex',
    alignItems: 'center',
    gap: s('sm'),
    px: s('sm'),
    borderBottom: `1px solid ${theme.app.colors.border}`,
    color: theme.app.colors.textMuted,
  });

export const buildGlobalSearchLoadingRowSx = ({
  height,
}: BuildGlobalSearchStatusRowSxArgs): SxProps<Theme> => ({
  height,
  display: 'flex',
  alignItems: 'center',
  gap: s('sm'),
  px: s('md'),
});

export const globalSearchErrorRowSx: SxProps<Theme> = {
  px: s('md'),
  py: s('md'),
};

export const buildGlobalSearchEmptyRowSx = ({
  height,
}: BuildGlobalSearchStatusRowSxArgs): SxProps<Theme> => ({
  height,
  px: s('md'),
  display: 'flex',
  alignItems: 'center',
});

export const buildGlobalSearchSearchFieldSx = ({
  inputAdornmentIconSize,
  isCompact,
  toolbarSearchBorderColor,
  toolbarSearchFontSize,
  toolbarSearchHeight,
  toolbarSearchRadius,
  toolbarSearchWidthPx,
}: BuildGlobalSearchSearchFieldSxArgs): SxProps<Theme> => {
  const width = isCompact ? 'min(78vw, 360px)' : toolbarSearchWidthPx;

  return {
    width,
    minWidth: width,
    maxWidth: width,
    '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
      minHeight: `${String(toolbarSearchHeight)}px`,
      height: `${String(toolbarSearchHeight)}px`,
      borderRadius: toolbarSearchRadius,
      backgroundColor: (theme) => theme.app.colors.bg,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: toolbarSearchRadius,
      borderColor: toolbarSearchBorderColor,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
      {
        borderColor: toolbarSearchBorderColor,
        borderWidth: '1px',
      },
    '& .MuiInputAdornment-positionStart .MuiSvgIcon-root': {
      width: inputAdornmentIconSize,
      height: inputAdornmentIconSize,
    },
    '& .MuiInputAdornment-positionStart': {
      marginLeft: s('none'),
      marginRight: s('sm'),
    },
    '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiInputBase-input, & .MuiOutlinedInput-input, & input':
      {
        fontSize: `${String(toolbarSearchFontSize)}px`,
      },
    '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder, & input::placeholder':
      {
        fontSize: `${String(toolbarSearchFontSize)}px`,
      },
  };
};

export const buildGlobalSearchDesktopShellSx = ({
  desktopSearchExpanded,
  toolbarSearchHeight,
  toolbarSearchWidth,
}: BuildGlobalSearchDesktopShellSxArgs): SxProps<Theme> => ({
  width: desktopSearchExpanded ? toolbarSearchWidth : toolbarSearchHeight,
  height: toolbarSearchHeight,
  minHeight: toolbarSearchHeight,
  minWidth: desktopSearchExpanded ? toolbarSearchWidth : toolbarSearchHeight,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  position: 'relative',
  flexShrink: 0,
  overflow: 'hidden',
});

export const buildGlobalSearchDesktopFieldFrameSx = ({
  toolbarSearchWidth,
}: BuildGlobalSearchDesktopFieldFrameSxArgs): SxProps<Theme> => ({
  width: toolbarSearchWidth,
  flexShrink: 0,
});

export const buildGlobalSearchDesktopIconButtonSx = ({
  toolbarSearchButtonIconSize,
  toolbarSearchHeight,
}: BuildGlobalSearchDesktopIconButtonSxArgs): SxProps<Theme> => ({
  width: toolbarSearchHeight,
  height: toolbarSearchHeight,
  color: 'text.secondary',
  '& .MuiSvgIcon-root': {
    width: `${String(toolbarSearchButtonIconSize)}px`,
    height: `${String(toolbarSearchButtonIconSize)}px`,
  },
});

export const buildGlobalSearchResultSummaryIconSx = ({
  resultIconContainerSize,
}: BuildGlobalSearchResultIconSxArgs): SxProps<Theme> => ({
  width: resultIconContainerSize,
  height: resultIconContainerSize,
  fontSize: 17,
  flexShrink: 0,
});

export const buildGlobalSearchResultGroupSx =
  ({ isFirstGroup }: BuildGlobalSearchResultGroupSxArgs): SxProps<Theme> =>
  (theme) => ({
    pt: isFirstGroup ? s('none') : s('sm'),
    borderTop: isFirstGroup ? 'none' : `1px solid ${theme.app.colors.border}`,
  });

export const buildGlobalSearchResultItemSx =
  ({ selected }: BuildGlobalSearchResultItemSxArgs): SxProps<Theme> =>
  (theme) => ({
    color: 'inherit',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: s('sm'),
    px: s('sm'),
    py: s('sm'),
    borderRadius: r('sm'),
    border: '1px solid transparent',
    backgroundColor: selected ? resolveSemanticColor(theme, 'surfaceHover') : 'transparent',
    boxShadow: selected ? `inset 2px 0 0 ${theme.app.colors.primary}` : 'none',
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: resolveSemanticColor(theme, 'surfaceHover'),
      borderColor: 'transparent',
    },
  });

export const buildGlobalSearchResultIconFrameSx =
  ({ resultIconContainerSize, selected }: BuildGlobalSearchResultIconFrameSxArgs): SxProps<Theme> =>
  (theme) => ({
    width: resultIconContainerSize,
    height: resultIconContainerSize,
    color: selected ? theme.app.colors.primary : theme.app.colors.textMuted,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  });
